import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Users,
  MessageSquare,
  Calendar,
  FileText,
  BarChart3,
  Search,
  Star,
  Award,
  ArrowRight,
  TrendingUp,
  Eye,
  Send,
  Bookmark,
  Clock,
  AlertTriangle,
  Crown
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

type FoundingAgentSession = {
  applicationId: string;
  username: string;
  passwordHash: string;
  fullName?: string;
  email?: string;
  positionNumber?: number | null;
};

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { user, isInternalUser, isLoading: userLoading } = useUnifiedUser();
  const [subscription, setSubscription] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [contactStats, setContactStats] = useState({ used: 0, limit: 0, remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [foundingAgentSession, setFoundingAgentSession] = useState<FoundingAgentSession | null>(null);

  // Check for founding agent session from application status page
  useEffect(() => {
    const raw =
      localStorage.getItem('foundingAgentSession') ??
      sessionStorage.getItem('foundingAgentSession');

    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      // Ensure legacy code paths still work
      sessionStorage.setItem('foundingAgentSession', JSON.stringify(parsed));
      setFoundingAgentSession(parsed);
    } catch (e) {
      console.error('Error parsing founding agent session:', e);
      sessionStorage.removeItem('foundingAgentSession');
      localStorage.removeItem('foundingAgentSession');
    }
  }, []);

  useEffect(() => {
    if (userLoading) return;

    // Internal founding agents must use the session (RLS blocks direct reads)
    if (foundingAgentSession?.applicationId) {
      if (!foundingAgentSession.username || !foundingAgentSession.passwordHash) {
        toast.error('Your session expired. Please log in again.');
        navigate('/agent/application-status');
        return;
      }
      fetchInternalAgentData(foundingAgentSession);
      return;
    }

    // If internal user is logged-in but no founding session exists, route them back
    if (isInternalUser) {
      setLoading(false);
      toast.error('Please access your dashboard via your Founding Agent application.');
      navigate('/agent/application-status');
      return;
    }

    // Supabase users
    if (user?.id) {
      fetchAgentData(user.id, 'supabase');
      return;
    }

    setLoading(false);
  }, [userLoading, user?.id, isInternalUser, foundingAgentSession, navigate]);

  const fetchInternalAgentData = async (session: FoundingAgentSession) => {
    try {
      const { data, error } = await supabase.functions.invoke('internal-agent-dashboard', {
        body: {
          applicationId: session.applicationId,
          username: session.username,
          passwordHash: session.passwordHash
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to load dashboard');

      setSubscription(data.subscription ?? null);
      setPlan(data.plan ?? data.subscription?.agent_subscription_plans ?? null);
      setProfile(data.profile ?? null);

      if (data.contactStats && typeof data.contactStats === 'object' && !Array.isArray(data.contactStats)) {
        const cs = data.contactStats as Record<string, unknown>;
        setContactStats({
          used: Number(cs.used) || 0,
          limit: Number(cs.limit) || 0,
          remaining: Number(cs.remaining) || 0
        });
      }
    } catch (error: any) {
      console.error('Error fetching internal agent data:', error);
      toast.error('Could not load your dashboard. Please log in again.');
      navigate('/agent/application-status');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentData = async (userId: string, userType: string) => {
    try {
      // Fetch subscription
      const { data: subData, error: subError } = await supabase
        .from('agent_subscriptions')
        .select('*, agent_subscription_plans(*)')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .single();

      if (subError) {
        console.warn('Subscription fetch error:', subError);
      }

      if (subData) {
        setSubscription(subData);
        setPlan(subData.agent_subscription_plans);
      }

      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('agent_profiles')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .single();

      if (profileError) {
        console.warn('Profile fetch error:', profileError);
      }

      if (profileData) {
        setProfile(profileData);
      }

      // Fetch contact limit status
      const { data: limitData, error: limitError } = await supabase.rpc('check_agent_contact_limit', {
        p_agent_user_id: userId,
        p_agent_user_type: userType
      });

      if (limitError) {
        console.warn('Contact limit fetch error:', limitError);
      }

      if (limitData && typeof limitData === 'object' && !Array.isArray(limitData)) {
        const data = limitData as Record<string, unknown>;
        setContactStats({
          used: Number(data.used) || 0,
          limit: Number(data.limit) || 0,
          remaining: Number(data.remaining) || 0
        });
      }
    } catch (error) {
      console.error('Error fetching agent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      icon: <Search className="h-5 w-5" />,
      title: 'Find Athletes',
      description: 'Search and discover athletes',
      action: () => navigate('/agent/athletes'),
      color: 'bg-blue-500/10 text-blue-500'
    },
    {
      icon: <MessageSquare className="h-5 w-5" />,
      title: 'Message Templates',
      description: 'Manage your outreach templates',
      action: () => navigate('/agent/messaging'),
      color: 'bg-purple-500/10 text-purple-500'
    },
    {
      icon: <Calendar className="h-5 w-5" />,
      title: 'Schedule',
      description: 'Manage your calendar',
      action: () => navigate('/agent/calendar'),
      color: 'bg-green-500/10 text-green-500'
    },
    {
      icon: <FileText className="h-5 w-5" />,
      title: 'Deal Workflows',
      description: 'Track your deals',
      action: () => navigate('/agent/deals'),
      color: 'bg-orange-500/10 text-orange-500'
    }
  ];

  const stats = [
    { label: 'Profile Views', value: '—', icon: <Eye className="h-4 w-4" />, change: null },
    { label: 'Messages Sent', value: '—', icon: <Send className="h-4 w-4" />, change: null },
    { label: 'Saved Athletes', value: '—', icon: <Bookmark className="h-4 w-4" />, change: null },
    { label: 'Response Rate', value: '—', icon: <TrendingUp className="h-4 w-4" />, change: null }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 pt-28 pb-12">
          <Card className="max-w-2xl mx-auto text-center">
            <CardHeader>
              <CardTitle>No Active Subscription</CardTitle>
              <CardDescription>
                If you’re a Founding Agent, please sign in via your application status page to access your dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate('/agent/application-status')}>
                Founding Agent Sign In
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/agent/pricing')}>
                View Pricing Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 pt-28 pb-8">
        {/* Welcome Section */}
        <section className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                Agent Dashboard
                {subscription.is_founding_agent && (
                  <Badge className="bg-primary/20 text-primary">
                    <Award className="h-3 w-3 mr-1" />
                    Founding Agent #{subscription.founding_agent_number}
                  </Badge>
                )}
              </h1>
              <p className="text-muted-foreground mt-1">
                Welcome back! Here's your activity overview.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="py-2 px-4">
                {plan?.name || 'Unknown Plan'}
              </Badge>
              {subscription.status === 'active' ? (
                <Badge className="bg-green-500/20 text-green-600 py-2 px-4">Active</Badge>
              ) : (
                <Badge variant="destructive" className="py-2 px-4">{subscription.status}</Badge>
              )}
            </div>
          </div>
        </section>

        {/* Founding Agent Banner */}
        {subscription.is_founding_agent && (
          <Alert className="mb-8 border-primary/30 bg-primary/5">
            <Award className="h-5 w-5 text-primary" />
            <AlertDescription className="ml-2">
              <strong>Founding Agent Beta Update:</strong> New tools and improvements are released 
              regularly during beta. Your access and pricing are protected as a Founding Agent 
              while we continue building.
            </AlertDescription>
          </Alert>
        )}

        {/* Contact Usage */}
        <Card className="mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Monthly Contact Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">
                {contactStats.limit === -1 ? 'Unlimited contacts' : `${contactStats.used} of ${contactStats.limit} contacts used`}
              </span>
              {contactStats.limit > 0 && (
                <span className="text-sm font-medium">
                  {contactStats.remaining} remaining
                </span>
              )}
            </div>
            {contactStats.limit > 0 && (
              <Progress 
                value={(contactStats.used / contactStats.limit) * 100} 
                className="h-2"
              />
            )}
            {contactStats.limit === -1 && (
              <div className="flex items-center gap-2 text-sm text-yellow-600">
                <Crown className="h-4 w-4" />
                <span>You have unlimited contacts with your Premium plan</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action, idx) => (
              <Card 
                key={idx} 
                className="cursor-pointer hover:border-primary/30 transition-colors"
                onClick={action.action}
              >
                <CardContent className="pt-6 text-center">
                  <div className={`w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center ${action.color}`}>
                    {action.icon}
                  </div>
                  <h3 className="font-medium mb-1">{action.title}</h3>
                  <p className="text-xs text-muted-foreground">{action.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Stats Grid */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Your Activity</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, idx) => (
              <Card key={idx}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">{stat.label}</span>
                    {stat.icon}
                  </div>
                  <div className="text-2xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Main Content Tabs */}
        <Tabs defaultValue="athletes" className="space-y-4">
          <TabsList>
            <TabsTrigger value="athletes">Saved Athletes</TabsTrigger>
            <TabsTrigger value="messages">Recent Messages</TabsTrigger>
            <TabsTrigger value="deals">Active Deals</TabsTrigger>
          </TabsList>

          <TabsContent value="athletes">
            <Card>
              <CardHeader>
                <CardTitle>Saved Athletes</CardTitle>
                <CardDescription>Athletes you've bookmarked for follow-up</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <Bookmark className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No saved athletes yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate('/agent/athletes')}>
                    Find Athletes
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Recent Messages</CardTitle>
                <CardDescription>Your latest outreach and responses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No messages yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate('/agent/messaging')}>
                    Create Template
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="deals">
            <Card>
              <CardHeader>
                <CardTitle>Active Deals</CardTitle>
                <CardDescription>Track your deal workflows</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No active deals</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate('/agent/deals')}>
                    Create Deal
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Subscription Info */}
        <section className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Subscription Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Plan:</span>
                  <span className="ml-2 font-medium">{plan?.name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Billing:</span>
                  <span className="ml-2 font-medium capitalize">{subscription.billing_cycle}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Renews:</span>
                  <span className="ml-2 font-medium">
                    {subscription.current_period_end 
                      ? new Date(subscription.current_period_end).toLocaleDateString()
                      : '—'
                    }
                  </span>
                </div>
              </div>
              {subscription.is_founding_agent && (
                <div className="mt-4 p-3 bg-primary/5 rounded-lg text-sm">
                  <strong>Founding Agent Benefit:</strong> Your rate of ${(subscription.founding_price_locked_cents || plan?.annual_price_cents) / 100}/year 
                  is locked for life as long as your subscription remains active.
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AgentDashboard;
